/**
 * Trip Map Component
 * Visar en tripp på OSM-karta med animation
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, CircleMarker, useMap } from 'react-leaflet';
import L from '@/utils/leaflet-fix';
import { OSM_RASTER_TILE_URL, OSM_TILE_ATTRIBUTION } from '@/utils/mapTiles';
import { tripsService, Trip, TripRoutePoint } from '@/services/trips/tripsService';
import toast from 'react-hot-toast';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { splitRouteSegmentByJamming, countJammingPoints } from '@/utils/routeJammingSegments';
import { isTenPercentOverLimit } from '@/utils/tripOverSpeed';
import TripSpeedChart from '@/components/maps/TripSpeedChart';

// Bil-ikon för animation (samma stil som i Dashboard/RealtimeMap)
const createVehicleIcon = (heading: number = 0, isMoving: boolean = true) => {
  const rotation = heading;
  // Blå färg för trip-animation
  const bodyColor = isMoving ? '#3B82F6' : '#6B7280';
  const lineColor = isMoving ? '#BFDBFE' : '#E5E7EB';
  
  return L.divIcon({
    className: 'vehicle-marker',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
        transform: rotate(${rotation}deg);
        transform-origin: center center;
        transition: transform 0.3s ease-out;
      ">
        <svg viewBox="0 0 100 100" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
          <!-- Bil kaross -->
          <path d="
            M 30 10
            Q 30 5, 50 5
            Q 70 5, 70 10
            L 75 20
            Q 80 22, 80 30
            L 80 75
            Q 80 85, 70 88
            L 70 92
            Q 70 95, 50 95
            Q 30 95, 30 92
            L 30 88
            Q 20 85, 20 75
            L 20 30
            Q 20 22, 25 20
            Z
          " fill="${bodyColor}" stroke="${lineColor}" stroke-width="2"/>
          
          <!-- Sidospeglar -->
          <ellipse cx="15" cy="28" rx="5" ry="3" fill="${bodyColor}" stroke="${lineColor}" stroke-width="1.5"/>
          <ellipse cx="85" cy="28" rx="5" ry="3" fill="${bodyColor}" stroke="${lineColor}" stroke-width="1.5"/>
          
          <!-- Vindruta (fram) -->
          <path d="
            M 35 12
            Q 35 8, 50 8
            Q 65 8, 65 12
            L 68 22
            L 32 22
            Z
          " fill="${lineColor}" opacity="0.9"/>
          
          <!-- Bakruta -->
          <path d="
            M 35 78
            L 65 78
            L 65 88
            Q 65 92, 50 92
            Q 35 92, 35 88
            Z
          " fill="${lineColor}" opacity="0.9"/>
          
          <!-- Motorhuv linje -->
          <line x1="40" y1="15" x2="60" y2="15" stroke="${lineColor}" stroke-width="2" opacity="0.7"/>
          
          <!-- Tak (soltak) -->
          <rect x="35" y="30" width="30" height="40" rx="3" fill="${bodyColor}" stroke="${lineColor}" stroke-width="1.5"/>
          
          <!-- Tak inre linje -->
          <rect x="38" y="33" width="24" height="8" rx="2" fill="none" stroke="${lineColor}" stroke-width="1" opacity="0.6"/>
          
          <!-- Kaross linjer (sidor) -->
          <path d="M 25 35 L 25 70" stroke="${lineColor}" stroke-width="1" opacity="0.5"/>
          <path d="M 75 35 L 75 70" stroke="${lineColor}" stroke-width="1" opacity="0.5"/>
          
          <!-- Bakljus detalj -->
          <rect x="32" y="85" width="10" height="3" rx="1" fill="${lineColor}" opacity="0.5"/>
          <rect x="58" y="85" width="10" height="3" rx="1" fill="${lineColor}" opacity="0.5"/>
        </svg>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Komponent för att uppdatera kartans viewport
const MapUpdater: React.FC<{ bounds: L.LatLngBounds | null }> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
};

// Komponent för bil-marker (uppdateras när position/heading ändras)
const AnimatedCarMarker: React.FC<{
  position: [number, number];
  heading: number;
  isPlaying: boolean;
}> = ({ position, heading, isPlaying }) => {
  const [icon, setIcon] = useState(() => createVehicleIcon(heading, isPlaying));

  useEffect(() => {
    setIcon(createVehicleIcon(heading, isPlaying));
  }, [heading, isPlaying]);

  return <Marker position={position} icon={icon} zIndexOffset={1000} />;
};

const FollowPlayback: React.FC<{ lat: number; lng: number; active: boolean }> = ({ lat, lng, active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    const point = L.latLng(lat, lng);
    if (!map.getBounds().pad(-0.08).contains(point)) {
      map.panTo(point, { animate: true, duration: 0.25 });
    }
  }, [lat, lng, active, map]);
  return null;
};

interface TripMapProps {
  trip: Trip;
}

const TripMap: React.FC<TripMapProps> = ({ trip }) => {
  const { t } = useTranslation();
  const [route, setRoute] = useState<TripRoutePoint[]>([]);
  const [routeSegments, setRouteSegments] = useState<TripRoutePoint[][]>([]);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSpeedChart, setShowSpeedChart] = useState(false);
  const [showPointsTable, setShowPointsTable] = useState(false);
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 0.5x, 1x, 2x, 4x
  const [showOverSpeed, setShowOverSpeed] = useState(false);
  const [followCar, setFollowCar] = useState(false);
  const animationRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const playIndexRef = useRef(0);
  const playingRef = useRef(false);
  const speedRef = useRef(playbackSpeed);
  const mapMountedRef = useRef(false);
  speedRef.current = playbackSpeed;

  // Ladda route-data när tripp ändras (avbryt föregående anrop så klick inte köar)
  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    setLoading(true);
    setIsPlaying(false);
    playingRef.current = false;
    playIndexRef.current = 0;
    setCurrentIndex(0);
    setShowPointsTable(false);

    const loadRoute = async () => {
      try {
        const routeData = await tripsService.getTripRoute(trip.tripId, { signal: ac.signal });
        if (cancelled) return;
        setRoute(routeData.route);
        setRouteSegments(
          routeData.routeSegments && routeData.routeSegments.length > 0
            ? routeData.routeSegments
            : [routeData.route]
        );
      } catch (error: any) {
        if (cancelled || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
          return;
        }
        console.error('Error loading route:', error);
        toast.error(t('trips.loadRouteError'));
        setRoute([]);
        setRouteSegments([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadRoute();

    return () => {
      cancelled = true;
      ac.abort();
      playingRef.current = false;
      if (animationRef.current != null) cancelAnimationFrame(animationRef.current);
      if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
    };
  }, [trip.tripId, t]);

  // Uppdatera bounds när route laddas
  useEffect(() => {
    if (route.length > 0) {
      mapMountedRef.current = true;
      const bounds = L.latLngBounds(
        route.map((point) => [point.coordinates[1], point.coordinates[0]] as [number, number])
      );
      setMapBounds(bounds);
    }
  }, [route]);

  const clearPlaybackTimers = () => {
    if (animationRef.current != null) cancelAnimationFrame(animationRef.current);
    if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
    animationRef.current = null;
    timeoutRef.current = null;
  };

  const seekTo = (index: number) => {
    if (route.length === 0) return;
    const next = Math.max(0, Math.min(route.length - 1, index));
    playIndexRef.current = next;
    setCurrentIndex(next);
    setFollowCar(true);
  };

  const startAnimation = () => {
    if (route.length === 0) return;
    clearPlaybackTimers();
    if (playIndexRef.current >= route.length - 1) {
      playIndexRef.current = 0;
      setCurrentIndex(0);
    }
    playingRef.current = true;
    setIsPlaying(true);
    setFollowCar(true);
    const baseDelay = Math.max(20, Math.min(500, 60000 / route.length));
    const tick = () => {
      if (!playingRef.current) return;
      setCurrentIndex(playIndexRef.current);
      if (playIndexRef.current >= route.length - 1) {
        playingRef.current = false;
        setIsPlaying(false);
        return;
      }
      const delay = baseDelay / (speedRef.current || 1);
      timeoutRef.current = window.setTimeout(() => {
        if (!playingRef.current) return;
        playIndexRef.current += 1;
        animationRef.current = requestAnimationFrame(tick);
      }, delay);
    };
    tick();
  };

  const stopAnimation = () => {
    playingRef.current = false;
    clearPlaybackTimers();
    setIsPlaying(false);
  };

  const resetAnimation = () => {
    stopAnimation();
    playIndexRef.current = 0;
    setCurrentIndex(0);
  };

  const tileLayerUrl = OSM_RASTER_TILE_URL;
  const attribution = OSM_TILE_ATTRIBUTION;

  // Konvertera route till Leaflet format [lat, lng]
  const routePolylines = useMemo(
    () =>
      routeSegments.map((segment) =>
        segment.map((point) => [point.coordinates[1], point.coordinates[0]] as [number, number])
      ),
    [routeSegments]
  );
  const routePolyline = useMemo(() => routePolylines.flat(), [routePolylines]);

  const coloredRouteSegments = useMemo(
    () => routeSegments.flatMap((segment) => splitRouteSegmentByJamming(segment)),
    [routeSegments]
  );
  const jammingPointCount = useMemo(
    () => routeSegments.reduce((sum, segment) => sum + countJammingPoints(segment), 0),
    [routeSegments]
  );
  const jammingMarkers = useMemo(() => {
    const all = route.flatMap((point, index) =>
      point.jamming
        ? [{
            key: `jamming-${index}-${point.timestamp}`,
            position: [point.coordinates[1], point.coordinates[0]] as [number, number],
          }]
        : []
    );
    const maxMarkers = 80;
    if (all.length <= maxMarkers) return all;
    const step = all.length / maxMarkers;
    const sampled: typeof all = [];
    for (let i = 0; i < maxMarkers; i++) {
      sampled.push(all[Math.min(all.length - 1, Math.floor(i * step))]);
    }
    return sampled;
  }, [route]);

  // Hämta aktuell position för animation
  // Om animationen körs, visa aktuell punkt, annars visa första punkten
  const currentPoint = route.length > 0 ? route[currentIndex] || route[0] : null;
  const currentPosition: [number, number] = currentPoint
    ? [currentPoint.coordinates[1], currentPoint.coordinates[0]]
    : routePolyline[0] || [59.3293, 18.0686];

  const currentHeading = currentPoint?.heading || 0;

  // Förbered data för hastighetsgraf (inkl. AVL 199 för tooltip/punkttabell)
  const speedChartData = useMemo(
    () =>
      route.map((point, index) => ({
        index,
        timestamp: new Date(point.timestamp).toLocaleTimeString('sv-SE', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        speed: point.speed || 0,
        speedLimit: point.speedLimit ?? null,
        overSpeed: isTenPercentOverLimit(point.speed, point.speedLimit) ? (point.speed || 0) : null,
        speedExcess: point.speedExcess ?? null,
        roadName: point.roadName || null,
        tripOdometer: point.tripOdometer ?? null,
      })),
    [route]
  );
  const hasAvl199 = useMemo(() => route.some((p) => p.tripOdometer != null), [route]);

  // Räkna hur många punkter som har hastighetsgräns
  const pointsWithSpeedLimit = speedChartData.filter(p => p.speedLimit !== null && p.speedLimit !== undefined).length;
  const hasSpeedLimitData = pointsWithSpeedLimit > 0;
  const overSpeedMarkers = useMemo(() => {
    if (!showOverSpeed) return [];
    return route.flatMap((point, index) => {
      if (!isTenPercentOverLimit(point.speed, point.speedLimit)) return [];
      return [{
        key: `over-${index}-${point.timestamp}`,
        position: [point.coordinates[1], point.coordinates[0]] as [number, number],
      }];
    });
  }, [route, showOverSpeed]);

  return (
    <div className="h-full flex flex-col">
      {/* Kontroller */}
      <div className="p-4 border-b border-border bg-bgElevated">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Hastighetskontroll */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-textSecondary">{t('trips.speed')}:</span>
            {[0.5, 1, 2, 4].map((speed) => (
              <button
                key={speed}
                onClick={() => setPlaybackSpeed(speed)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  playbackSpeed === speed
                    ? 'bg-blue-600 text-white'
                    : 'bg-bgBase text-textDefault hover:bg-bgBase'
                }`}
              >
                {speed}x
              </button>
            ))}
          </div>
          
          {/* Uppspelningsknappar */}
          <div className="flex items-center gap-2">
            <button
              onClick={startAnimation}
              disabled={loading || route.length === 0 || isPlaying}
              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-bgBase rounded-md transition-colors"
            >
              ▶ {t('trips.playTrip')}
            </button>
            <button
              onClick={stopAnimation}
              disabled={!isPlaying}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-bgBase rounded-md transition-colors"
            >
              ⏸ {t('trips.pauseTrip')}
            </button>
            <button
              onClick={resetAnimation}
              disabled={loading || route.length === 0}
              className="px-3 py-1.5 text-sm font-medium text-textDefault bg-bgBase hover:bg-bgBase disabled:bg-bgBase rounded-md transition-colors"
            >
              ↺ {t('trips.resetTrip')}
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm text-textSecondary">
            <input
              type="checkbox"
              checked={showOverSpeed}
              onChange={(event) => setShowOverSpeed(event.target.checked)}
              disabled={!hasSpeedLimitData}
            />
            {t('trips.showOverSpeed')}
          </label>

          {jammingPointCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-textSecondary">
              <span className="inline-block w-8 h-1 rounded bg-red-500" />
              <span>{t('trips.jammingLegend', { count: jammingPointCount })}</span>
            </div>
          )}

          <div
            className={`px-2 py-1 text-xs font-medium rounded ${
              (trip.alarmCount ?? 0) > 0
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                : 'bg-bgBase text-textSecondary'
            }`}
            title={t('trips.alarmCountTitle') || 'Körbeteendelarm på denna resa'}
          >
            {(trip.alarmCount ?? 0) > 0
              ? t('trips.alarmCount', { count: trip.alarmCount }) || `${trip.alarmCount} larm`
              : t('trips.noAlarms') || 'Inga larm'}
          </div>
        </div>
      </div>

      {/* Karta – behåll Leaflet monterad vid tripbyte så tiles inte laddas om */}
      <div className="flex-1 relative">
        {route.length === 0 && !mapMountedRef.current ? (
          <div className="absolute inset-0 flex items-center justify-center bg-bgLight">
            <div className="text-textSecondary">
              {loading ? t('trips.loadingRoute') : t('commonUi.noRouteData')}
            </div>
          </div>
        ) : route.length === 0 && !loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-bgLight">
            <div className="text-textSecondary">{t('commonUi.noRouteData')}</div>
          </div>
        ) : (
          <MapContainer
            center={currentPosition}
            zoom={13}
            scrollWheelZoom={true}
            className="h-full w-full"
          >
            <TileLayer
              attribution={attribution}
              url={tileLayerUrl}
              maxZoom={19}
            />
            <MapUpdater bounds={mapBounds} />
            
            {/* Rutt-linje – blå normal, röd vid AVL 249 jamming */}
            {coloredRouteSegments.map((segment, index) => (
              <Polyline
                key={`route-colored-${index}-${segment.jamming ? 'jam' : 'ok'}`}
                positions={segment.positions}
                color={segment.jamming ? '#EF4444' : '#3B82F6'}
                weight={segment.jamming ? 5 : 4}
                opacity={segment.jamming ? 0.95 : 0.7}
              />
            ))}

            {/* Markörer på exakta jamming-koordinater */}
            {jammingMarkers.map((marker) => (
              <CircleMarker
                key={marker.key}
                center={marker.position}
                radius={6}
                pathOptions={{
                  color: '#FFFFFF',
                  weight: 2,
                  fillColor: '#EF4444',
                  fillOpacity: 1,
                }}
              />
            ))}

            {/* Start-marker */}
            {routePolyline.length > 0 && (
              <Marker
                position={routePolyline[0]}
                icon={L.divIcon({
                  className: 'start-marker',
                  html: '<div style="width: 16px; height: 16px; background: green; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                  iconSize: [16, 16],
                  iconAnchor: [8, 8],
                })}
              />
            )}

            {/* Slut-marker */}
            {routePolyline.length > 1 && (
              <Marker
                position={routePolyline[routePolyline.length - 1]}
                icon={L.divIcon({
                  className: 'end-marker',
                  html: '<div style="width: 16px; height: 16px; background: red; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                  iconSize: [16, 16],
                  iconAnchor: [8, 8],
                })}
              />
            )}

            {/* Bil-marker (visas alltid när route finns) */}
            {showOverSpeed && overSpeedMarkers.map((marker) => (
              <CircleMarker
                key={marker.key}
                center={marker.position}
                radius={5}
                pathOptions={{ color: '#dc2626', fillColor: '#ef4444', fillOpacity: 0.9, weight: 1 }}
              />
            ))}

            {route.length > 0 && (
              <FollowPlayback lat={currentPosition[0]} lng={currentPosition[1]} active={followCar || isPlaying} />
            )}

            {route.length > 0 && (
              <AnimatedCarMarker
                position={currentPosition}
                heading={currentHeading}
                isPlaying={isPlaying}
              />
            )}
          </MapContainer>
        )}
        {loading && (route.length > 0 || mapMountedRef.current) && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-bgLight/70 pointer-events-none">
            <div className="px-3 py-2 rounded-md bg-bgElevated shadow text-sm text-textSecondary">
              {t('trips.loadingRoute')}
            </div>
          </div>
        )}
      </div>

      {/* Hastighetsgraf (fällbar) */}
      {route.length > 0 && (
        <div className="border-t border-border bg-bgElevated">
          <button
            onClick={() => setShowSpeedChart(!showSpeedChart)}
            className="w-full px-4 py-2 flex items-center justify-between hover:bg-bgBase transition-colors"
          >
            <span className="text-sm font-medium text-textDefault">
              {showSpeedChart ? t('trips.hideSpeedChart') : t('trips.showSpeedChart')}
            </span>
            {showSpeedChart ? (
              <ChevronDownIcon className="h-5 w-5 text-textSecondary" />
            ) : (
              <ChevronUpIcon className="h-5 w-5 text-textSecondary" />
            )}
          </button>
          
          {showSpeedChart && (
            <div className="p-4 border-t border-border">
              {!hasSpeedLimitData && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md space-y-2">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {t('trips.noSpeedLimitData')}
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    {t('trips.speedLimitRefreshHint')}
                  </p>
                </div>
              )}
              {hasSpeedLimitData && pointsWithSpeedLimit < route.length && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t('trips.partialSpeedLimitData', { 
                      withLimit: pointsWithSpeedLimit, 
                      total: route.length 
                    })}
                  </p>
                </div>
              )}
              <TripSpeedChart
                data={speedChartData}
                currentIndex={currentIndex}
                hasSpeedLimitData={hasSpeedLimitData}
                onSeek={seekTo}
              />
            </div>
          )}
        </div>
      )}

      {/* Punkttabell (tid, hastighet, AVL 199) – fällbar */}
      {route.length > 0 && (
        <div className="border-t border-border bg-bgElevated">
          <button
            onClick={() => setShowPointsTable(!showPointsTable)}
            className="w-full px-4 py-2 flex items-center justify-between hover:bg-bgBase transition-colors"
          >
            <span className="text-sm font-medium text-textDefault">
              {showPointsTable ? t('trips.hidePointsTable') : t('trips.showPointsTable')}
            </span>
            {showPointsTable ? (
              <ChevronDownIcon className="h-5 w-5 text-textSecondary" />
            ) : (
              <ChevronUpIcon className="h-5 w-5 text-textSecondary" />
            )}
          </button>
          {showPointsTable && (
            <div className="p-4 border-t border-border overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-1.5 pr-2 text-textSecondary">#</th>
                    <th className="py-1.5 pr-2 text-textSecondary">{t('trips.time')}</th>
                    <th className="py-1.5 pr-2 text-textSecondary">{t('trips.speed')} ({t('trips.speedUnit')})</th>
                    {hasAvl199 && <th className="py-1.5 text-textSecondary">{t('trips.avl199')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {route.map((point, idx) => (
                    <tr key={idx} className="border-b border-border">
                      <td className="py-1 pr-2 text-textDefault">{idx + 1}</td>
                      <td className="py-1 pr-2 text-textDefault">
                        {new Date(point.timestamp).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="py-1 pr-2 text-textDefault">{point.speed ?? '–'}</td>
                      {hasAvl199 && (
                        <td className="py-1 text-textDefault">
                          {point.tripOdometer != null
                            ? `${(point.tripOdometer / 1000).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`
                            : '–'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripMap;

